/**
 * KnowledgeBaseSources - Shared component for displaying Knowledge Base sources
 * Used by both desktop Interface and mobile MobileCentralInterface
 *
 * Features:
 * - Collapsible "N sources" button
 * - Expandable list with source cards
 * - Popup modal for source details
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, X as XIcon, FileText } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';

export interface KnowledgeBaseSourceItem {
  title?: string;
  url?: string;
  file_name?: string;
  file_type?: string;
  relevance_score?: number;
  snippet?: string;
  qdrant_id?: string;
}

interface KnowledgeBaseSourcesProps {
  sources: KnowledgeBaseSourceItem[];
  /** Optional class name for container */
  className?: string;
  /** Whether to use dark theme (default: true for chat bubbles) */
  isDark?: boolean;
}

/**
 * Get display name for a source - prefer title, fall back to filename
 */
const getDisplayName = (source: KnowledgeBaseSourceItem): string => {
  if (source.title && source.title !== 'Untitled Document') {
    return source.title;
  }
  return source.file_name || 'Document';
};

export const KnowledgeBaseSources: React.FC<KnowledgeBaseSourcesProps> = ({
  sources,
  className = '',
  isDark = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [currentSourceIndex, setCurrentSourceIndex] = useState(0);

  if (!sources || sources.length === 0) return null;

  const handleSourceClick = (index: number) => {
    setCurrentSourceIndex(index);
    setPopupOpen(true);
  };

  const currentSource = sources[currentSourceIndex];

  // Theme-aware styles
  const containerClass = isDark
    ? 'mt-3 pt-3 border-t border-white/10'
    : 'mt-3 pt-3 border-t border-gray-200';

  const toggleButtonClass = isDark
    ? 'flex items-center gap-1.5 text-xs text-white/60 hover:text-white/80 transition-colors'
    : 'flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors';

  const sourceCardClass = isDark
    ? 'flex items-center gap-2.5 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-md cursor-pointer transition-all max-w-full'
    : 'flex items-center gap-2.5 px-3 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 rounded-md cursor-pointer transition-all max-w-full';

  const sourceNameClass = isDark
    ? 'w-0 flex-1 text-xs text-white/90 truncate'
    : 'w-0 flex-1 text-xs text-gray-900 truncate';

  const fileIconClass = isDark
    ? 'w-3 h-3 text-white/30 flex-shrink-0'
    : 'w-3 h-3 text-gray-400 flex-shrink-0';

  return (
    <div className={`${containerClass} ${className} overflow-hidden min-w-0`}>
      {/* Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={toggleButtonClass}
      >
        {isExpanded ? (
          <ChevronDown className="w-3 h-3 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-3 h-3 flex-shrink-0" />
        )}
        <span>{sources.length} source{sources.length !== 1 ? 's' : ''}</span>
      </button>

      {/* Expanded Sources List */}
      {isExpanded && (
        <div className="mt-2 flex flex-col gap-2 overflow-hidden min-w-0">
          {sources.map((source, index) => {
            const displayName = getDisplayName(source);
            return (
              <div
                key={source.qdrant_id || index}
                onClick={() => handleSourceClick(index)}
                className={`${sourceCardClass} overflow-hidden min-w-0`}
              >
                {/* Citation Number Badge */}
                <span className="flex-shrink-0 w-5 h-5 rounded text-[10px] font-semibold bg-primary text-primary-foreground flex items-center justify-center">
                  {index + 1}
                </span>

                {/* Document Name + snippet preview */}
                <span className="w-0 flex-1 min-w-0">
                  <span className={`block ${sourceNameClass.replace('w-0 flex-1 ', '')}`}>
                    {displayName}
                  </span>
                  {source.snippet && (
                    <span className={`block truncate text-[11px] ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      "{source.snippet}"
                    </span>
                  )}
                </span>

                {/* View source affordance */}
                <span
                  className={`flex-shrink-0 rounded border px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] ${
                    isDark
                      ? 'border-white/15 text-white/60'
                      : 'border-gray-300 text-gray-500'
                  }`}
                >
                  View Source
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Source Detail Popup/Modal */}
      <Dialog open={popupOpen} onOpenChange={setPopupOpen}>
        <DialogContent className={`${isDark ? 'dark bg-background text-foreground' : ''} max-w-[calc(100vw-2rem)] sm:max-w-[480px] p-0 gap-0 overflow-hidden [&>button]:hidden`}>
          {currentSource && (
            <>
              {/* Header */}
              <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'bg-muted/30 border-border' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2.5">
                  {/* Citation Number */}
                  <span className="flex items-center justify-center w-7 h-7 text-[13px] font-semibold bg-primary text-primary-foreground rounded-md">
                    {currentSourceIndex + 1}
                  </span>
                  {/* Source counter */}
                  <span className={`text-[13px] ${isDark ? 'text-muted-foreground' : 'text-gray-500'}`}>
                    {currentSourceIndex + 1} of {sources.length}
                  </span>
                </div>

                {/* Close */}
                <button
                  onClick={() => setPopupOpen(false)}
                  className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${isDark ? 'hover:bg-muted' : 'hover:bg-gray-200'}`}
                  title="Close (Esc)"
                >
                  <XIcon className="w-3 h-3" />
                </button>
              </div>

              {/* Content */}
              <div className="px-5 py-4 overflow-hidden min-w-0">
                {/* Document Title */}
                <div className="flex items-start gap-2.5 mb-4 min-w-0">
                  <FileText className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-[15px] font-semibold leading-snug break-words ${isDark ? 'text-foreground' : 'text-gray-900'}`}>
                      {getDisplayName(currentSource)}
                    </h3>
                    {/* Show filename if different from title */}
                    {currentSource.title &&
                     currentSource.title !== 'Untitled Document' &&
                     currentSource.file_name &&
                     currentSource.title !== currentSource.file_name && (
                      <p className={`text-xs mt-0.5 ${isDark ? 'text-muted-foreground' : 'text-gray-500'}`}>
                        {currentSource.file_name}
                      </p>
                    )}
                  </div>
                </div>

                {/* Excerpt - Clean quote style */}
                {currentSource.snippet && (
                  <div
                    className={`relative p-3 rounded-lg text-[13px] leading-relaxed break-words overflow-hidden ${isDark ? 'bg-muted/30 text-muted-foreground' : 'bg-gray-50 text-gray-600'}`}
                    style={{ borderLeft: '3px solid hsl(var(--primary))' }}
                  >
                    {currentSource.snippet}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KnowledgeBaseSources;
